import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, getApiErrorMessage } from '../lib/api'
import { kakaoLogin } from '../lib/kakao'
import { naverLogin } from '../lib/naver'
import { Roles, RoleLabels } from '../auth/rbac'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { PrivacyContent, TermsContent } from './legal/LegalContents'
import LegalPolicyPublicSection from './legal/LegalPolicyPublicSection'
import PublicFooter from '../ui/PublicFooter'
import { GoogleLogin } from '@react-oauth/google'

const ORG_DATA_SHARING_CONSENT_VERSION = 'SIGNUP_ORG_DATA_SHARING_2026-06-16'

const getOrgParentId = (org) => org?.parentOrgId ?? org?.parent_org_id

const getOrgType = (org) => String(org?.type ?? '').toUpperCase()

const getOrgTypeLabel = (org) => {
  const type = getOrgType(org)
  if (type === 'GA') return 'GA'
  if (type === 'INSURER') return '원수사'
  if (type === 'PERSONAL') return '개인'
  return '조직'
}

export default function SignupPage() {
  const navigate = useNavigate()
  const maxBirthDate = useMemo(() => {
    const cutoff = new Date()
    cutoff.setFullYear(cutoff.getFullYear() - 14)
    const yyyy = cutoff.getFullYear()
    const mm = String(cutoff.getMonth() + 1).padStart(2, '0')
    const dd = String(cutoff.getDate()).padStart(2, '0')
    return [yyyy, mm, dd].join('-')
  }, [])
  const [orgOptions, setOrgOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [legal, setLegal] = useState({
    termsVersion: '2026-07-10.3',
    privacyVersion: '2026-07-19.1',
    marketingVersion: '2026-04-14',
    refundPolicyVersion: '2026-07-10',
    termsPath: '/terms',
    privacyPath: '/privacy',
    refundPolicyPath: '/refund-policy',
  })
  const [form, setForm] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    birthDate: '',
    referrerCode: '',
    orgId: '',
    requestedRole: ''
  })
  /** 이메일 = local @ (선택 도메인 | 직접입력 도메인) */
  const [emailLocal, setEmailLocal] = useState('')
  const [emailDomainSel, setEmailDomainSel] = useState('naver.com')
  const [emailDomainCustom, setEmailDomainCustom] = useState('')
  const [personalJoin, setPersonalJoin] = useState(true)
  const [affiliationText, setAffiliationText] = useState('')
  const [orgDataSharingConsentAccepted, setOrgDataSharingConsentAccepted] = useState(false)
  const [orgSearchOpen, setOrgSearchOpen] = useState(false)
  const [orgSearchTerm, setOrgSearchTerm] = useState('')
  const [orgEligibilityCheck, setOrgEligibilityCheck] = useState({
    status: 'idle',
    query: '',
    message: '',
  })
  const [personalJoinConfirmOpen, setPersonalJoinConfirmOpen] = useState(false)
  const [personalJoinConfirmed, setPersonalJoinConfirmed] = useState(false)

  // 개인 가입은 내부 호환용 최소 권한 설계사 역할로 신청한다
  useEffect(() => {
    if (personalJoin) {
      setForm(prev => ({
        ...prev,
        requestedRole: Roles.GA_PLANNER
      }))
    }
  }, [personalJoin])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const referralCode = String(params.get('ref') || params.get('referrerCode') || '').trim()
    if (!referralCode) return
    setForm(prev => ({ ...prev, referrerCode: referralCode }))
  }, [])
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    age14: false,
    marketing: false,
  })
  const [legalModal, setLegalModal] = useState({ open: false, doc: null }) // doc: 'terms' | 'privacy'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [socialLinkModal, setSocialLinkModal] = useState({
    open: false,
    signupId: null,
    signupLinkToken: null,
    linking: false,
    error: null,
    linked: { GOOGLE: false, KAKAO: false, NAVER: false },
  })

  const googleEnabled = String(process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim().length > 0
  const kakaoEnabled = String(process.env.REACT_APP_KAKAO_JS_KEY || '').trim().length > 0
  const naverEnabled = String(process.env.REACT_APP_NAVER_CLIENT_ID || '').trim().length > 0
  const socialConfigured = googleEnabled || kakaoEnabled || naverEnabled

  const [emailCheck, setEmailCheck] = useState({
    status: 'idle', // idle | invalid | checking | available | unavailable | error
    checkedEmail: '',
    message: null,
  })

  const [phoneCheck, setPhoneCheck] = useState({
    status: 'idle', // idle | invalid | checking | available | unavailable | error
    checkedPhone: '',
    message: null,
  })

  const isValidEmail = (v) => {
    if (!v) return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim())
  }

  const EMAIL_DOMAIN_PRESETS = useMemo(
    () => [
      { value: 'naver.com', label: 'naver.com' },
      { value: 'gmail.com', label: 'gmail.com' },
      { value: 'daum.net', label: 'daum.net' },
      { value: 'hanmail.net', label: 'hanmail.net' },
      { value: 'kakao.com', label: 'kakao.com' },
      { value: 'nate.com', label: 'nate.com' },
      { value: 'outlook.com', label: 'outlook.com' },
    ],
    []
  )

  const fullEmail = useMemo(() => {
    const loc = String(emailLocal || '').trim()
    if (!loc) return ''
    if (emailDomainSel === '__custom__') {
      const dom = String(emailDomainCustom || '')
        .trim()
        .replace(/^@+/, '')
      return dom ? `${loc}@${dom}` : ''
    }
    return `${loc}@${emailDomainSel}`
  }, [emailLocal, emailDomainSel, emailDomainCustom])

  const normalizePhone = (v) => String(v || '').replace(/[^0-9]/g, '')

  // 표시용 포맷: 01012345678 -> 010-1234-5678 (타이핑/붙여넣기 대응)
  const formatPhoneDisplay = (v) => {
    const d = normalizePhone(v)
    if (!d) return ''
    if (d.length <= 3) return d
    if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`
    // 3-4-나머지(최대 010-1234-5678 형태)
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`
  }

  const isValidPhone = (v) => {
    const d = normalizePhone(v)
    return d.length >= 9 && d.length <= 12
  }

  const checkEmailNow = async (rawEmail) => {
    const email = String(rawEmail || '').trim()
    if (!email) {
      const next = { status: 'invalid', checkedEmail: '', message: '이메일을 입력해 주세요.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }
    if (!isValidEmail(email)) {
      const next = { status: 'invalid', checkedEmail: email, message: '이메일 형식을 확인해 주세요.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }

    // 이미 확인된 값이면 그대로 반환
    if (emailCheck.checkedEmail === email && emailCheck.status === 'available') {
      return { available: true }
    }
    if (emailCheck.checkedEmail === email && emailCheck.status === 'unavailable') {
      return { available: false, message: emailCheck.message || '이미 사용 중인 이메일입니다.' }
    }

    setEmailCheck(prev => ({ ...prev, status: 'checking', checkedEmail: email, message: null }))
    try {
      const res = await api.get('/signups/public/check-email', { params: { email } })
      const payload = res?.data?.data ?? res?.data
      const available = Boolean(payload?.available)
      const reason = payload?.reason
      const next = available
        ? { status: 'available', checkedEmail: email, message: '사용 가능한 이메일입니다.' }
        : { status: 'unavailable', checkedEmail: email, message: reason || '이미 사용 중인 이메일입니다.' }
      setEmailCheck(next)
      return { available, message: next.message }
    } catch (err) {
      const next = { status: 'error', checkedEmail: email, message: '이메일 중복 확인에 실패했습니다.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }
  }

  const debouncedEmail = useDebouncedValue(fullEmail, 400)

  const checkPhoneNow = async (rawPhone) => {
    const phoneNumber = String(rawPhone || '').trim()
    if (!phoneNumber) {
      const next = { status: 'invalid', checkedPhone: '', message: '휴대폰 번호를 입력해 주세요.' }
      setPhoneCheck(next)
      return { available: false, message: next.message }
    }
    if (!isValidPhone(phoneNumber)) {
      const next = { status: 'invalid', checkedPhone: phoneNumber, message: '휴대폰 번호 형식을 확인해 주세요.' }
      setPhoneCheck(next)
      return { available: false, message: next.message }
    }

    // 이미 확인된 값이면 그대로 반환
    if (phoneCheck.checkedPhone === phoneNumber && phoneCheck.status === 'available') {
      return { available: true }
    }
    if (phoneCheck.checkedPhone === phoneNumber && phoneCheck.status === 'unavailable') {
      return { available: false, message: phoneCheck.message || '이미 사용 중인 휴대폰 번호입니다.' }
    }

    setPhoneCheck(prev => ({ ...prev, status: 'checking', checkedPhone: phoneNumber, message: null }))
    try {
      const res = await api.get('/signups/public/check-phone', { params: { phoneNumber } })
      const payload = res?.data?.data ?? res?.data
      const available = Boolean(payload?.available)
      const reason = payload?.reason
      const next = available
        ? { status: 'available', checkedPhone: phoneNumber, message: '사용 가능한 휴대폰 번호입니다.' }
        : { status: 'unavailable', checkedPhone: phoneNumber, message: reason || '이미 사용 중인 휴대폰 번호입니다.' }
      setPhoneCheck(next)
      return { available, message: next.message }
    } catch {
      const next = { status: 'error', checkedPhone: phoneNumber, message: '휴대폰 번호 중복 확인에 실패했습니다.' }
      setPhoneCheck(next)
      return { available: false, message: next.message }
    }
  }

  const debouncedPhone = useDebouncedValue(form.phoneNumber, 400)

  useEffect(() => {
    setEmailCheck((prev) => ({ ...prev, status: 'idle', checkedEmail: '', message: null }))
  }, [emailLocal, emailDomainSel, emailDomainCustom])

  // 입력 중에도 자동으로(디바운스) 중복 확인하여 사용자 경험 개선
  useEffect(() => {
    const email = String(debouncedEmail || '').trim()
    if (!email) {
      setEmailCheck({ status: 'idle', checkedEmail: '', message: null })
      return
    }
    if (!isValidEmail(email)) {
      setEmailCheck(prev => ({ ...prev, status: 'invalid', checkedEmail: email, message: '이메일 형식을 확인해 주세요.' }))
      return
    }
    if (emailCheck.checkedEmail === email && (emailCheck.status === 'available' || emailCheck.status === 'unavailable')) {
      return
    }

    let ignore = false
    ;(async () => {
      setEmailCheck(prev => ({ ...prev, status: 'checking', checkedEmail: email, message: null }))
      try {
        const res = await api.get('/signups/public/check-email', { params: { email } })
        const payload = res?.data?.data ?? res?.data
        const available = Boolean(payload?.available)
        const reason = payload?.reason
        const next = available
          ? { status: 'available', checkedEmail: email, message: '사용 가능한 이메일입니다.' }
          : { status: 'unavailable', checkedEmail: email, message: reason || '이미 사용 중인 이메일입니다.' }
        if (!ignore) setEmailCheck(next)
      } catch {
        if (!ignore) setEmailCheck({ status: 'error', checkedEmail: email, message: '이메일 중복 확인에 실패했습니다.' })
      }
    })()

    return () => { ignore = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail])

  useEffect(() => {
    const phoneNumber = String(debouncedPhone || '').trim()
    if (!phoneNumber) {
      setPhoneCheck({ status: 'idle', checkedPhone: '', message: null })
      return
    }
    if (!isValidPhone(phoneNumber)) {
      setPhoneCheck(prev => ({ ...prev, status: 'invalid', checkedPhone: phoneNumber, message: '휴대폰 번호 형식을 확인해 주세요.' }))
      return
    }
    if (phoneCheck.checkedPhone === phoneNumber && (phoneCheck.status === 'available' || phoneCheck.status === 'unavailable')) {
      return
    }

    let ignore = false
    ;(async () => {
      setPhoneCheck(prev => ({ ...prev, status: 'checking', checkedPhone: phoneNumber, message: null }))
      try {
        const res = await api.get('/signups/public/check-phone', { params: { phoneNumber } })
        const payload = res?.data?.data ?? res?.data
        const available = Boolean(payload?.available)
        const reason = payload?.reason
        const next = available
          ? { status: 'available', checkedPhone: phoneNumber, message: '사용 가능한 휴대폰 번호입니다.' }
          : { status: 'unavailable', checkedPhone: phoneNumber, message: reason || '이미 사용 중인 휴대폰 번호입니다.' }
        if (!ignore) setPhoneCheck(next)
      } catch {
        if (!ignore) setPhoneCheck({ status: 'error', checkedPhone: phoneNumber, message: '휴대폰 번호 중복 확인에 실패했습니다.' })
      }
    })()

    return () => { ignore = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPhone])

  // 최상위(생보/손보/GA)별 역할 그룹
  const gaRoleGroup = useMemo(
    () => [
      // 정책: GA는 대표/관리자/매니저/설계사 4단계만 사용한다.
      // 노출/정렬: 설계사 → 매니저 → 관리자 → 대표
      Roles.GA_PLANNER,
      Roles.GA_OPERATION_MANAGER,
      Roles.GA_MANAGER,
      Roles.GA_LEADER,
    ],
    []
  )

  const insurerRoleGroup = useMemo(
    () => [
      // 정책: 원수사는 대표/관리자/매니저/설계사 4단계만 사용한다.
      // 노출/정렬: 설계사 → 매니저 → 관리자 → 대표
      Roles.INSURER_PLANNER,
      Roles.INSURER_OPERATION_MANAGER,
      Roles.INSURER_MANAGER,
      Roles.INSURER_LEADER,         // 대표
    ],
    []
  )

  const orderByRoleGroup = React.useCallback((codes, preferredOrder) => {
    const set = new Set((codes || []).map(String))
    const out = []
    for (const c of (preferredOrder || [])) {
      if (set.has(String(c))) out.push(String(c))
    }
    // 혹시 모를 잔여(정의되지 않은 역할/추가 역할)는 뒤에 붙인다.
    for (const c of (codes || [])) {
      const s = String(c)
      if (!out.includes(s)) out.push(s)
    }
    return out
  }, [])

  useEffect(() => {
    async function load() {
      try {
        const [orgRes, legalRes] = await Promise.all([
          api.get('/orgs/public-options'),
          api.get('/signups/public/legal').catch(() => null),
        ])
        const orgData = orgRes?.data?.data ?? orgRes?.data ?? []
        setOrgOptions(orgData)

        const legalPayload = legalRes?.data?.data ?? legalRes?.data ?? null
        if (legalPayload) {
          setLegal(prev => ({
            ...prev,
            termsVersion: legalPayload?.termsVersion || prev.termsVersion,
            privacyVersion: legalPayload?.privacyVersion || prev.privacyVersion,
            marketingVersion: legalPayload?.marketingVersion || prev.marketingVersion,
            refundPolicyVersion: legalPayload?.refundPolicyVersion || prev.refundPolicyVersion,
            termsPath: legalPayload?.termsPath || prev.termsPath,
            privacyPath: legalPayload?.privacyPath || prev.privacyPath,
            refundPolicyPath: legalPayload?.refundPolicyPath || prev.refundPolicyPath,
          }))
        }

        // 직접 회원가입에서 선택 가능한 역할(정책): "실사용 역할"만 노출한다.
        // - GA: 대표/관리자/설계사
        // - 원수사: 대표/관리자/매니저/설계사
        const allowedCodes = Array.from(new Set([
          ...gaRoleGroup,
          ...insurerRoleGroup,
        ].map(String)))
        setRoleOptions(allowedCodes)
      } catch (e) {
        setError('조직/역할 정보를 불러오지 못했습니다.')
      }
    }
    load()
  }, [gaRoleGroup, insurerRoleGroup])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'phoneNumber') {
      // 휴대폰 변경 시 기존 중복확인 결과는 무효
      setPhoneCheck(prev => ({ ...prev, status: 'idle', checkedPhone: '', message: null }))
      // XXX-XXXX-XXXX 형태로 정규화해서 표시
      const formatted = formatPhoneDisplay(value)
      setForm(prev => ({ ...prev, [name]: formatted }))
      return
    }
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const submitSignupRequest = async () => {
    // 이메일 중복 확인(최종 방어선)
    const emailResult = await checkEmailNow(fullEmail)
    if (!emailResult.available) {
      setError(emailResult.message || '이메일 중복 확인에 실패했습니다.')
      return
    }

    // 휴대폰 번호 중복 확인(최종 방어선)
    const phoneResult = await checkPhoneNow(form.phoneNumber)
    if (!phoneResult.available) {
      setError(phoneResult.message || '휴대폰 번호 중복 확인에 실패했습니다.')
      return
    }

    setLoading(true)
    try {
      const res = await api.post('/signups/public', {
        email: fullEmail,
        name: form.name,
        password: form.password,
        phoneNumber: String(form.phoneNumber || '').trim(),
        referrerCode: String(form.referrerCode || '').trim() || undefined,
        orgId: personalJoin ? Number(personalRootOrgId) : Number(form.orgId),
        requestedRole: form.requestedRole,
        personalJoin: personalJoin || undefined,
        affiliationText: personalJoin ? (affiliationText || '').trim() || undefined : undefined,
        affiliationOrgCreateConsent: undefined, // 더 이상 사용하지 않음 (하위 호환성을 위해 null로 전송)
        orgDataSharingConsentAccepted: personalJoin ? undefined : Boolean(orgDataSharingConsentAccepted),
        orgDataSharingConsentVersion: personalJoin ? undefined : ORG_DATA_SHARING_CONSENT_VERSION,
        termsAccepted: Boolean(agreements.terms),
        termsVersion: legal.termsVersion || 'unknown',
        privacyAccepted: Boolean(agreements.privacy),
        privacyVersion: legal.privacyVersion || 'unknown',
        age14OrOverAccepted: Boolean(agreements.age14),
        age14OrOverVersion: 'AGE14_BIRTH_DATE_2026-07-10',
        birthDate: form.birthDate,
        marketingAccepted: Boolean(agreements.marketing),
        marketingVersion: legal.marketingVersion || null,
        consentChannel: 'WEB',
      })
      const payload = res?.data?.data ?? res?.data ?? {}
      const signupId = payload?.id
      const signupLinkToken = payload?.signupLinkToken
      setSuccess('가입 신청이 접수되었습니다. 승인 후 로그인할 수 있습니다.')
      try {
        window?.toast?.show?.('가입 신청이 접수되었습니다. 승인 후 로그인할 수 있습니다.', 'success')
      } catch { /* no toast provider */ }
      // 가입 직후: 소셜 연동(예약) 팝업 노출 (키가 설정된 경우에만)
      if (socialConfigured) {
        setSocialLinkModal({
          open: true,
          signupId: signupId ?? null,
          signupLinkToken: signupLinkToken ?? null,
          linking: false,
          error: null,
          linked: { GOOGLE: false, KAKAO: false, NAVER: false },
        })
      } else {
        // 키 미설정 상태에서는 일반 사용자에게 소셜 UI를 숨긴다.
        navigate('/login', { state: { emailHint: String(fullEmail || '').trim() } })
      }
    } catch (err) {
      setError(getApiErrorMessage(err, '가입 신청에 실패했습니다.'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (form.password !== form.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.')
      return
    }
    if (form.password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.')
      return
    }
    if (personalJoin) {
      if (!String(affiliationText || '').trim()) {
        setError('조직명을 입력해 주세요.')
        return
      }
      if (String(affiliationText || '').trim().length > 200) {
        setError('조직명은 200자 이하로 입력해 주세요.')
        return
      }
      if (personalRootOrgId == null) {
        setError('개인 가입용 조직 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.')
        return
      }
      if (!form.requestedRole) {
        setError('역할을 선택해 주세요.')
        return
      }
    } else {
      if (!form.orgId) {
        setError('조직을 검색해 선택해 주세요.')
        return
      }
      if (!form.requestedRole) {
        setError('신청 역할을 선택해 주세요.')
        return
      }
      const selectedOrg = orgOptions.find(o => String(o?.id) === String(form.orgId))
      if (!selectedOrg || !getOrgParentId(selectedOrg)) {
        setError('소속 신청 가능한 조직을 선택해 주세요.')
        return
      }
      if (!orgDataSharingConsentAccepted) {
        setError('조직 소속 신청을 위해 조직원 데이터 공유 동의가 필요합니다.')
        return
      }
    }
    if (!form.birthDate) {
      setError('생년월일을 입력해 주세요.')
      return
    }
    if (form.birthDate > maxBirthDate) {
      setError('만 14세 미만은 회원가입할 수 없습니다.')
      return
    }
    if (!agreements.terms || !agreements.privacy || !agreements.age14) {
      setError('필수 약관 동의와 만 14세 이상 확인이 필요합니다.')
      return
    }

    if (personalJoin && !personalJoinConfirmed) {
      setPersonalJoinConfirmOpen(true)
      return
    }

    await submitSignupRequest()
  }

  const closeSocialModalAndGoLogin = () => {
    setSocialLinkModal(prev => ({ ...prev, open: false }))
    navigate('/login')
  }

  const reserveSocialLink = async (provider, { idToken, accessToken } = {}) => {
    const signupId = socialLinkModal.signupId
    const signupLinkToken = socialLinkModal.signupLinkToken
    if (!signupId || !signupLinkToken) {
      setSocialLinkModal(prev => ({ ...prev, error: '가입 신청 정보가 없어 소셜 연동을 진행할 수 없습니다.' }))
      return
    }
    setSocialLinkModal(prev => ({ ...prev, linking: true, error: null }))
    try {
      await api.post(`/signups/public/social-links/${String(provider).toLowerCase()}`, {
        signupId,
        signupLinkToken,
        idToken,
        accessToken,
      })
      setSocialLinkModal(prev => ({
        ...prev,
        linking: false,
        linked: { ...prev.linked, [String(provider).toUpperCase()]: true },
        error: null,
      }))
    } catch (err) {
      const msg =
        err?.response?.data?.data?.message
        || err?.response?.data?.message
        || err?.response?.data?.error?.message
        || err?.message
        || '소셜 연동에 실패했습니다.'
      setSocialLinkModal(prev => ({ ...prev, linking: false, error: String(msg) }))
    }
  }

  const roleLabel = (code) => {
    return RoleLabels[code] || code
  }

  // 개인 루트 조직 ID (개인 가입 시 사용, 생보/손보/GA와 동일한 루트 레벨)
  const personalRootOrgId = useMemo(() => {
    return orgOptions.find(o => {
      if (o?.name !== '개인') return false
      const type = String(o?.type ?? '').toUpperCase()
      if (type !== 'PERSONAL') return false
      const pid = getOrgParentId(o)
      return pid == null || pid === '' || pid === undefined
    })?.id
  }, [orgOptions])

  const orgById = useMemo(() => {
    const map = new Map()
    for (const org of orgOptions) {
      if (org?.id == null) continue
      map.set(String(org.id), org)
    }
    return map
  }, [orgOptions])

  const selectedOrg = useMemo(
    () => (form.orgId ? orgById.get(String(form.orgId)) || null : null),
    [form.orgId, orgById]
  )

  const getOrgDisplayPath = React.useCallback((org) => {
    if (!org) return ''
    const signupDisplayPath = String(org?.signupDisplayPath || '').trim()
    if (signupDisplayPath) return signupDisplayPath
    const names = []
    const seen = new Set()
    let current = org
    for (let depth = 0; current && depth < 8; depth += 1) {
      const id = String(current?.id ?? '')
      if (id && seen.has(id)) break
      if (id) seen.add(id)
      if (current?.name) names.unshift(current.name)
      const parentId = getOrgParentId(current)
      if (parentId == null || parentId === '') break
      current = orgById.get(String(parentId))
    }
    return names.join(' / ') || String(org?.name || '')
  }, [orgById])
  const selectedOrgPath = selectedOrg ? getOrgDisplayPath(selectedOrg) : ''

  const isSignupEligibleOrganization = (org) => {
    const value = org?.signupEligible
    return value === true || String(value || '').toLowerCase() === 'true'
  }

  const signupOrgRows = useMemo(() => {
    const rows = []
    for (const org of orgOptions) {
      if (!org?.id || !isSignupEligibleOrganization(org)) continue
      const path = getOrgDisplayPath(org)
      rows.push({
        org,
        path,
        typeLabel: getOrgTypeLabel(org),
        searchText: `${path} ${org?.name || ''} ${getOrgTypeLabel(org)}`.toLowerCase(),
      })
    }
    return rows.sort((a, b) => a.path.localeCompare(b.path, 'ko'))
  }, [orgOptions, getOrgDisplayPath])

  const orgSearchResults = useMemo(() => {
    const keyword = String(orgSearchTerm || '').trim().toLowerCase()
    if (!keyword) return []
    const rows = signupOrgRows.filter(row => row.searchText.includes(keyword))
    return rows.slice(0, 50)
  }, [orgSearchTerm, signupOrgRows])

  const debouncedOrgSearchTerm = useDebouncedValue(orgSearchTerm, 350)

  useEffect(() => {
    const organizationName = String(debouncedOrgSearchTerm || '').trim()
    if (!orgSearchOpen || !organizationName || orgSearchResults.length > 0) {
      setOrgEligibilityCheck(prev => (
        prev.status === 'idle' && !prev.query && !prev.message
          ? prev
          : { status: 'idle', query: '', message: '' }
      ))
      return undefined
    }

    let cancelled = false
    setOrgEligibilityCheck({ status: 'checking', query: organizationName, message: '' })
    api.get('/orgs/public-signup-eligibility', { params: { organizationName } })
      .then((res) => {
        if (cancelled) return
        const payload = res?.data?.data ?? res?.data ?? {}
        const signupEligible = payload?.signupEligible === true
        setOrgEligibilityCheck({
          status: signupEligible ? 'eligible' : 'ineligible',
          query: organizationName,
          message: String(payload?.message || '').trim(),
        })
      })
      .catch(() => {
        if (cancelled) return
        setOrgEligibilityCheck({
          status: 'error',
          query: organizationName,
          message: '조직계약 가입 대상 여부를 확인하지 못했습니다. 개인회원으로 가입해 주세요.',
        })
      })

    return () => {
      cancelled = true
    }
  }, [orgSearchOpen, debouncedOrgSearchTerm, orgSearchResults.length])
  const selectedOrgType = getOrgType(selectedOrg)

  const filteredRoleOptions = useMemo(() => {
    if (personalJoin) {
      return [Roles.GA_PLANNER]
    }
    if (selectedOrgType === 'GA') {
      return orderByRoleGroup(roleOptions.filter(code => gaRoleGroup.includes(code)), gaRoleGroup)
    }
    if (selectedOrgType === 'INSURER') {
      return orderByRoleGroup(roleOptions.filter(code => insurerRoleGroup.includes(code)), insurerRoleGroup)
    }
    return [Roles.GA_PLANNER]
  }, [personalJoin, selectedOrgType, roleOptions, gaRoleGroup, insurerRoleGroup, orderByRoleGroup])

  const firstRoleForOrgType = React.useCallback((type) => {
    const normalizedType = String(type || '').toUpperCase()
    if (normalizedType === 'GA') {
      return orderByRoleGroup(roleOptions.filter(code => gaRoleGroup.includes(code)), gaRoleGroup)[0] || ''
    }
    if (normalizedType === 'INSURER') {
      return orderByRoleGroup(roleOptions.filter(code => insurerRoleGroup.includes(code)), insurerRoleGroup)[0] || ''
    }
    return [Roles.GA_PLANNER][0] || ''
  }, [roleOptions, gaRoleGroup, insurerRoleGroup, orderByRoleGroup])

  useEffect(() => {
    if (personalJoin && personalRootOrgId != null) {
      setForm(prev => ({ ...prev, orgId: Number(personalRootOrgId) }))
    }
  }, [personalJoin, personalRootOrgId])

  useEffect(() => {
    if (personalJoin || !selectedOrg || filteredRoleOptions.length === 0) return
    if (filteredRoleOptions.includes(form.requestedRole)) return
    setForm(prev => ({ ...prev, requestedRole: filteredRoleOptions[0] || '' }))
  }, [personalJoin, selectedOrg, filteredRoleOptions, form.requestedRole])

  const handleOrgAffiliationToggle = (e) => {
    const checked = e.target.checked
    setPersonalJoin(!checked)
    setPersonalJoinConfirmed(false)
    setPersonalJoinConfirmOpen(false)
    setOrgDataSharingConsentAccepted(false)
    setOrgSearchOpen(false)
    setOrgSearchTerm('')
    setForm(prev => ({
      ...prev,
      orgId: checked ? '' : (personalRootOrgId != null ? Number(personalRootOrgId) : ''),
      requestedRole: checked ? '' : Roles.GA_MANAGER,
    }))
  }

  const handleOrgSelect = (org) => {
    if (!org?.id || !isSignupEligibleOrganization(org)) return
    setPersonalJoin(false)
    setPersonalJoinConfirmed(false)
    setOrgDataSharingConsentAccepted(false)
    setForm(prev => ({
      ...prev,
      orgId: Number(org.id),
      requestedRole: firstRoleForOrgType(getOrgType(org)),
    }))
    setOrgEligibilityCheck({ status: 'idle', query: '', message: '' })
    setOrgSearchOpen(false)
    setOrgSearchTerm('')
  }

  const handleSwitchToPersonalSignup = () => {
    setPersonalJoin(true)
    setPersonalJoinConfirmed(false)
    setPersonalJoinConfirmOpen(false)
    setOrgDataSharingConsentAccepted(false)
    setOrgEligibilityCheck({ status: 'idle', query: '', message: '' })
    setOrgSearchOpen(false)
    setOrgSearchTerm('')
    setForm(prev => ({
      ...prev,
      orgId: personalRootOrgId != null ? Number(personalRootOrgId) : '',
      requestedRole: Roles.GA_PLANNER,
    }))
  }
  const handlePersonalJoinConfirm = async () => {
    setPersonalJoinConfirmed(true)
    setPersonalJoinConfirmOpen(false)
    await submitSignupRequest()
  }

  const handlePersonalJoinSwitchToOrg = () => {
    setPersonalJoin(false)
    setPersonalJoinConfirmed(false)
    setPersonalJoinConfirmOpen(false)
    setOrgDataSharingConsentAccepted(false)
    setOrgSearchTerm('')
    setForm(prev => ({
      ...prev,
      orgId: '',
      requestedRole: '',
    }))
    setOrgSearchOpen(true)
  }

  const requiredAgreed = agreements.terms && agreements.privacy && agreements.age14
  const closeLegalModal = () => setLegalModal({ open: false, doc: null })

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <main className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      {personalJoinConfirmOpen && (
        <div className="fixed inset-0 z-[1300]">
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="personal-join-confirm-title"
              className="w-full max-w-md rounded-lg border bg-white p-5 shadow-xl"
            >
              <div id="personal-join-confirm-title" className="text-lg font-semibold text-slate-900">
                개인회원 가입 확인
              </div>
              <div className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
                <p>조직계약중인 조직에 속해있다면 해당 조직에 승인을 요청하여야 합니다.</p>
                <p>개인회원이 맞는지 다시 한번 확인해주세요.</p>
              </div>
              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2"
                  onClick={handlePersonalJoinConfirm}
                  disabled={loading}
                >
                  개인회원으로 진행
                </button>
                <button
                  type="button"
                  className="rounded-md bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2"
                  onClick={handlePersonalJoinSwitchToOrg}
                  disabled={loading}
                >
                  조직 검색하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {socialLinkModal.open && (
        <div className="fixed inset-0 z-[1100]">
          <div className="absolute inset-0 bg-black/40" onClick={() => closeSocialModalAndGoLogin()} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-xl border p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-slate-900">소셜 로그인 연동</div>
                  <div className="mt-1 text-sm text-slate-600">
                    지금 연동해두면 <span className="font-semibold">승인 후 바로</span> 구글/카카오로 로그인할 수 있어요.
                  </div>
                </div>
                <button type="button" className="text-sm text-slate-500 hover:text-slate-800" onClick={closeSocialModalAndGoLogin}>
                  닫기
                </button>
              </div>

              {socialLinkModal.error && (
                <div className="mt-3 text-sm text-red-700 whitespace-pre-line">
                  {socialLinkModal.error}
                </div>
              )}

              <div className="mt-4 space-y-3">
                <div className="text-xs text-slate-500">
                  연동은 로그인(사용)과 다릅니다. 승인 전에는 로그인할 수 없습니다.
                </div>

                {(googleEnabled) && (
                  <div className="rounded border p-3">
                    <div className="text-sm font-medium text-slate-800 flex items-center justify-between">
                      <span>Google</span>
                      {socialLinkModal.linked.GOOGLE && <span className="text-xs text-emerald-700">연동됨</span>}
                    </div>
                    <div className="mt-2">
                      <GoogleLogin
                        useOneTap={false}
                        onSuccess={(cred) => {
                          const idToken = cred?.credential
                          if (!idToken) {
                            setSocialLinkModal(prev => ({ ...prev, error: '구글 토큰을 받지 못했습니다.' }))
                            return
                          }
                          reserveSocialLink('google', { idToken })
                        }}
                        onError={() => setSocialLinkModal(prev => ({ ...prev, error: '구글 로그인(연동)에 실패했습니다.' }))}
                        text={socialLinkModal.linked.GOOGLE ? 'continue_with' : 'continue_with'}
                        width="100%"
                      />
                    </div>
                  </div>
                )}

                {(kakaoEnabled) && (
                  <div className="rounded border p-3">
                    <div className="text-sm font-medium text-slate-800 flex items-center justify-between">
                      <span>Kakao</span>
                      {socialLinkModal.linked.KAKAO && <span className="text-xs text-emerald-700">연동됨</span>}
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        className="w-full rounded bg-[#FEE500] text-[#191919] py-2 text-sm font-semibold disabled:opacity-50"
                        disabled={socialLinkModal.linking}
                        onClick={async () => {
                          try {
                            const accessToken = await kakaoLogin({ scope: 'account_email' })
                            await reserveSocialLink('kakao', { accessToken })
                          } catch (e) {
                            setSocialLinkModal(prev => ({ ...prev, error: '카카오 로그인(연동)에 실패했습니다.' }))
                          }
                        }}
                      >
                        카카오로 연동하기
                      </button>
                    </div>
                  </div>
                )}

                {(naverEnabled) && (
                  <div className="rounded border p-3">
                    <div className="text-sm font-medium text-slate-800 flex items-center justify-between">
                      <span>Naver</span>
                      {socialLinkModal.linked.NAVER && <span className="text-xs text-emerald-700">연동됨</span>}
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        className="w-full rounded bg-[#03C75A] text-white py-2 text-sm font-semibold disabled:opacity-50"
                        disabled={socialLinkModal.linking}
                        onClick={async () => {
                          try {
                            const { code, state, redirectUri } = await naverLogin({})
                            await reserveSocialLink('naver', { code, state, redirectUri })
                          } catch (e) {
                            setSocialLinkModal(prev => ({ ...prev, error: '네이버 로그인(연동)에 실패했습니다.' }))
                          }
                        }}
                      >
                        네이버로 연동하기
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={closeSocialModalAndGoLogin}
                    disabled={socialLinkModal.linking}
                  >
                    나중에 할게요
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-50"
                    onClick={closeSocialModalAndGoLogin}
                    disabled={socialLinkModal.linking}
                  >
                    로그인 화면으로
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {legalModal.open && (
        <div className="fixed inset-0 z-[1000]">
          <div className="absolute inset-0 bg-black/40" onClick={closeLegalModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl border">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="font-semibold text-slate-900">
                  {legalModal.doc === 'terms' ? '이용약관' : '개인정보 처리방침'}
                  <span className="ml-2 text-xs text-slate-500">
                    (버전: {legalModal.doc === 'terms' ? (legal.termsVersion || '-') : (legal.privacyVersion || '-')})
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    className="text-xs text-slate-600 hover:text-brand-sky"
                    href={legalModal.doc === 'terms' ? (legal.termsPath || '/terms') : (legal.privacyPath || '/privacy')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    전체 화면
                  </a>
                  <button type="button" className="text-sm text-slate-600 hover:text-slate-900" onClick={closeLegalModal}>
                    닫기
                  </button>
                </div>
              </div>
              <div className="max-h-[70vh] overflow-auto px-4 py-4">
                <LegalPolicyPublicSection
                  docCode={legalModal.doc === 'terms' ? 'TERMS' : 'PRIVACY'}
                  fallback={legalModal.doc === 'terms' ? <TermsContent /> : <PrivacyContent />}
                  showMetadata={false}
                />
              </div>
              <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
                {legalModal.doc === 'terms' && (
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => {
                      setAgreements(prev => ({ ...prev, terms: true }))
                      closeLegalModal()
                    }}
                  >
                    이용약관 동의
                  </button>
                )}
                {legalModal.doc === 'privacy' && (
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => {
                      setAgreements(prev => ({ ...prev, privacy: true }))
                      closeLegalModal()
                    }}
                  >
                    개인정보 처리방침 동의
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            회원가입 신청
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            기본 계정 정보를 입력해 주세요. 조직 소속은 필요할 때 함께 신청할 수 있습니다.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                이메일 <span className="text-rose-600">*</span>
              </label>
              <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  required
                  aria-label="이메일 아이디"
                  className="w-full min-w-0 flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  placeholder="이메일 아이디"
                  value={emailLocal}
                  onChange={(e) => setEmailLocal(e.target.value)}
                />
                <span className="hidden text-slate-500 sm:inline" aria-hidden>@</span>
                <div className="flex w-full min-w-0 flex-1 gap-2 sm:w-auto">
                  <select
                    aria-label="이메일 도메인"
                    className="w-full min-w-0 flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy sm:max-w-[11rem]"
                    value={emailDomainSel}
                    onChange={(e) => setEmailDomainSel(e.target.value)}
                  >
                    {EMAIL_DOMAIN_PRESETS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                    <option value="__custom__">직접 입력</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => checkEmailNow(fullEmail)}
                  disabled={loading || emailCheck.status === 'checking' || !isValidEmail(fullEmail)}
                  className="shrink-0 rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {emailCheck.status === 'checking' ? '확인 중...' : '중복 확인'}
                </button>
              </div>
              {emailDomainSel === '__custom__' && (
                <input
                  type="text"
                  className="mt-2 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  placeholder="도메인 (예: company.com)"
                  value={emailDomainCustom}
                  onChange={(e) => setEmailDomainCustom(e.target.value.replace(/^@+/, ''))}
                  autoCapitalize="none"
                />
              )}
              {fullEmail && (
                <p className="mt-1 text-xs text-slate-500">
                  입력된 주소: <span className="font-mono text-slate-700">{fullEmail}</span>
                </p>
              )}
              {emailCheck.message && (
                <div
                  className={[
                    'mt-1 text-xs',
                    emailCheck.status === 'available' ? 'text-emerald-700' : '',
                    (emailCheck.status === 'unavailable' || emailCheck.status === 'invalid' || emailCheck.status === 'error') ? 'text-red-700' : '',
                    (emailCheck.status !== 'available' && emailCheck.status !== 'unavailable' && emailCheck.status !== 'invalid' && emailCheck.status !== 'error') ? 'text-slate-600' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {emailCheck.message}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                이름 <span className="text-rose-600">*</span>
              </label>
              <input
                name="name"
                type="text"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="홍길동"
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                휴대폰 번호 <span className="text-rose-600">*</span>
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  name="phoneNumber"
                  type="tel"
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  placeholder="010-0000-0000"
                  value={form.phoneNumber}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => checkPhoneNow(form.phoneNumber)}
                  disabled={loading || phoneCheck.status === 'checking' || !isValidPhone(form.phoneNumber)}
                  className="shrink-0 rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {phoneCheck.status === 'checking' ? '확인 중...' : '중복 확인'}
                </button>
              </div>
              {phoneCheck.message && (
                <div
                  className={[
                    'mt-1 text-xs',
                    phoneCheck.status === 'available' ? 'text-emerald-700' : '',
                    (phoneCheck.status === 'unavailable' || phoneCheck.status === 'invalid' || phoneCheck.status === 'error') ? 'text-red-700' : '',
                    (phoneCheck.status !== 'available' && phoneCheck.status !== 'unavailable' && phoneCheck.status !== 'invalid' && phoneCheck.status !== 'error') ? 'text-slate-600' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {phoneCheck.message}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                생년월일 <span className="text-rose-600">*</span>
              </label>
              <input
                name="birthDate"
                type="date"
                required
                max={maxBirthDate}
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                value={form.birthDate}
                onChange={handleChange}
              />
            </div>

            <div>
              {personalJoin && (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="personal-organization-name">
                    조직명 <span className="text-rose-600">*</span>
                  </label>
                  <input
                    id="personal-organization-name"
                    type="text"
                    required
                    maxLength={200}
                    className="mt-1 w-full rounded-md border px-3 py-2 focus:border-brand-navy focus:outline-none focus:ring-brand-navy"
                    placeholder="설계의뢰와 리포트에 표시할 조직명"
                    value={affiliationText}
                    onChange={(e) => setAffiliationText(e.target.value)}
                  />
                </div>
              )}

              <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!personalJoin}
                  onChange={handleOrgAffiliationToggle}
                  className="mt-0.5 rounded border-slate-300 text-brand-navy focus:ring-brand-navy"
                />
                <span className="font-medium text-slate-800">
                  단체계약중인 조직에 속해있다면 체크해주세요
                </span>
              </label>

              {!personalJoin && (
                <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-800">소속 조직</div>
                      <div className="mt-1 text-sm text-slate-600">
                        {selectedOrgPath || '조직을 검색해 선택해 주세요.'}
                      </div>
                      {selectedOrg && (
                        <div className="mt-1 text-xs text-slate-500">
                          {getOrgTypeLabel(selectedOrg)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2"
                      onClick={() => setOrgSearchOpen(true)}
                    >
                      조직 검색
                    </button>
                  </div>

                  <label className="mt-3 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={orgDataSharingConsentAccepted}
                      onChange={(e) => setOrgDataSharingConsentAccepted(e.target.checked)}
                      className="mt-1 rounded border-slate-300 text-brand-navy focus:ring-brand-navy"
                    />
                    <span>
                      조직 관리자에게 진료정보분석, 숨은보험금찾기, 업무 처리 결과, 이용 현황 조회 권한이 제공되는 것에 동의합니다.
                    </span>
                  </label>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">
                      신청 역할 <span className="text-rose-600">*</span>
                    </label>
                    <select
                      name="requestedRole"
                      className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy disabled:bg-slate-100 disabled:cursor-not-allowed"
                      value={form.requestedRole}
                      onChange={handleChange}
                      disabled={!selectedOrg}
                    >
                      {!selectedOrg ? (
                        <option value="">먼저 조직을 선택해 주세요</option>
                      ) : (
                        filteredRoleOptions.map(code => (
                          <option key={code} value={code}>
                            {roleLabel(code)}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              )}

              {orgSearchOpen && (
                <div className="fixed inset-0 z-[1200]">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setOrgSearchOpen(false)} />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="signup-org-search-title"
                      className="w-full max-w-lg rounded-lg border bg-white shadow-xl"
                    >
                      <div className="flex items-center justify-between border-b px-4 py-3">
                        <div id="signup-org-search-title" className="font-semibold text-slate-900">
                          조직 검색
                        </div>
                        <button
                          type="button"
                          className="rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"
                          onClick={() => setOrgSearchOpen(false)}
                        >
                          닫기
                        </button>
                      </div>
                      <div className="p-4">
                        <input
                          type="search"
                          className="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                          aria-label="조직명 검색"
                          placeholder="조직명 검색"
                          value={orgSearchTerm}
                          onChange={(e) => setOrgSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.preventDefault()
                          }}
                          autoFocus
                        />
                        <div className="mt-3 max-h-72 overflow-y-auto">
                          {!String(orgSearchTerm || '').trim() ? (
                            <div className="rounded-md border border-dashed border-slate-300 px-3 py-8 text-center text-sm text-slate-500">
                              조직명을 입력해 주세요.
                            </div>
                          ) : orgSearchResults.length === 0 ? (
                            <div className="rounded-md border border-dashed border-slate-300 px-3 py-8 text-center text-sm text-slate-500">
                              <p>
                                {orgEligibilityCheck.status === 'checking'
                                  ? '조직계약 가입 대상 여부를 확인하고 있습니다.'
                                  : orgEligibilityCheck.message || '검색 결과가 없습니다.'}
                              </p>
                              {(orgEligibilityCheck.status === 'ineligible' || orgEligibilityCheck.status === 'error') && (
                                <button
                                  type="button"
                                  className="mt-3 rounded-md border border-brand-navy px-3 py-2 text-sm font-medium text-brand-navy hover:bg-brand-navy/5 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2"
                                  onClick={handleSwitchToPersonalSignup}
                                >
                                  개인회원으로 가입
                                </button>
                              )}
                            </div>
                          ) : (
                            <ul className="divide-y divide-slate-100 rounded-md border">
                              {orgSearchResults.map(row => (
                                <li key={row.org.id}>
                                  <button
                                    type="button"
                                    className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-inset"
                                    onClick={() => handleOrgSelect(row.org)}
                                  >
                                    <span className="min-w-0">
                                      <span className="block text-xs text-slate-500">{row.typeLabel}</span>
                                      <span className="block truncate text-sm font-medium text-slate-900">{row.path}</span>
                                    </span>
                                    <span className="shrink-0 text-sm font-medium text-brand-navy">선택</span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                비밀번호 <span className="text-rose-600">*</span>
              </label>
              <input
                name="password"
                type="password"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="6자 이상"
                value={form.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                비밀번호 확인 <span className="text-rose-600">*</span>
              </label>
              <input
                name="confirmPassword"
                type="password"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="비밀번호를 다시 입력하세요"
                value={form.confirmPassword}
                onChange={handleChange}
              />
            </div>

            <div className="rounded-md border bg-white p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">약관 동의</div>
                <button
                  type="button"
                  className="text-xs text-slate-600 hover:text-brand-sky"
                  onClick={() => setAgreements({ terms: true, privacy: true, age14: true, marketing: true })}
                >
                  전체 동의
                </button>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.terms}
                    onChange={(e) => setAgreements(prev => ({ ...prev, terms: e.target.checked }))}
                  />
                  <span className="flex-1">
                    <span className="text-rose-600">[필수]</span> 이용약관 동의
                    <button
                      type="button"
                      className="ml-2 text-xs text-slate-600 hover:text-brand-sky underline"
                      onClick={() => setLegalModal({ open: true, doc: 'terms' })}
                    >
                      보기
                    </button>
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.privacy}
                    onChange={(e) => setAgreements(prev => ({ ...prev, privacy: e.target.checked }))}
                  />
                  <span className="flex-1">
                    <span className="text-rose-600">[필수]</span> 개인정보 처리방침 동의
                    <button
                      type="button"
                      className="ml-2 text-xs text-slate-600 hover:text-brand-sky underline"
                      onClick={() => setLegalModal({ open: true, doc: 'privacy' })}
                    >
                      보기
                    </button>
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.age14}
                    onChange={(e) => setAgreements(prev => ({ ...prev, age14: e.target.checked }))}
                  />
                  <span className="flex-1">
                    <span className="text-rose-600">[필수]</span> 만 14세 이상입니다
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.marketing}
                    onChange={(e) => setAgreements(prev => ({ ...prev, marketing: e.target.checked }))}
                  />
                  <span className="flex-1">
                    [선택] 마케팅/광고성 정보 수신 동의
                  </span>
                </label>

                <div className="text-xs text-slate-500">
                  {requiredAgreed ? '필수 항목에 동의했습니다.' : '필수 항목에 동의해 주세요.'}
                  {legal.termsVersion || legal.privacyVersion ? (
                    <span className="ml-1">
                      (버전: {legal.termsVersion || '-'} / {legal.privacyVersion || '-'})
                    </span>
                  ) : null}
                </div>
                <div className="text-[11px] text-slate-400">선택 동의는 서비스 이용에 필수가 아닙니다.</div>
              </div>
            </div>
          </div>

          {error && <InlineError message={error} />}
          {success && <div className="text-sm text-green-700">{success}</div>}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-navy hover:bg-brand-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-navy disabled:opacity-50"
            >
              {loading ? <Loading label="신청 중..." /> : '가입 신청'}
            </button>
          </div>
        </form>
      </div>
      </main>
      <PublicFooter />
    </div>
  )
}
